import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingBag, Star, Minus, Plus, ArrowLeft, Share2, Truck, MapPin, Package, Handshake, Film, Loader2, Play, X, Clapperboard, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { productsAPI, ordersAPI, paymentsAPI, deliveryAPI, roomsAPI, videoAPI, storyAPI, negotiateAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ShareButton from '../components/shared/ShareButton';
import ReportButton from '../components/shared/ReportButton';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [buying, setBuying] = useState(false);
  const [includeDelivery, setIncludeDelivery] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryEstimate, setDeliveryEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  // Sora 2 AI Video state
  const [videoStatus, setVideoStatus] = useState(null); // null | 'none' | 'processing' | 'completed' | 'failed'
  const [videoJobId, setVideoJobId] = useState(null);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const pollRef = useRef(null);

  // Story Video state
  const [storyStatus, setStoryStatus] = useState(null);
  const [storyJobId, setStoryJobId] = useState(null);
  const [storyProgress, setStoryProgress] = useState(0);
  const [storyStep, setStoryStep] = useState('');
  const [generatingStory, setGeneratingStory] = useState(false);
  const [showStoryPlayer, setShowStoryPlayer] = useState(false);
  
  // AI Negotiator
  const [showAINeg, setShowAINeg] = useState(false);
  const [aiDesiredPrice, setAiDesiredPrice] = useState('');
  const [aiMinPrice, setAiMinPrice] = useState('');
  const [aiNegMsg, setAiNegMsg] = useState('');
  const [startingNeg, setStartingNeg] = useState(false);
  const storyPollRef = useRef(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await productsAPI.getById(id);
        setProduct(res.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [id, navigate]);

  // Check if product already has an AI video
  useEffect(() => {
    if (!id) return;
    const checkVideo = async () => {
      try {
        const res = await videoAPI.productVideo(id);
        if (res.data.status === 'completed') {
          setVideoStatus('completed');
          setVideoJobId(res.data.job_id);
        } else if (res.data.status === 'processing') {
          setVideoStatus('processing');
          setVideoJobId(res.data.job_id);
        } else {
          setVideoStatus('none');
        }
      } catch {
        setVideoStatus('none');
      }
    };
    checkVideo();
  }, [id]);

  // Poll for video status when processing
  const pollVideoStatus = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!videoJobId) return;
      try {
        const res = await videoAPI.status(videoJobId);
        if (res.data.status === 'completed') {
          setVideoStatus('completed');
          clearInterval(pollRef.current);
          toast.success('AI video ready!');
        } else if (res.data.status === 'failed') {
          setVideoStatus('failed');
          clearInterval(pollRef.current);
          toast.error('Video generation failed');
        }
      } catch { /* ignore */ }
    }, 8000);
  }, [videoJobId]);

  useEffect(() => {
    if (videoStatus === 'processing' && videoJobId) pollVideoStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [videoStatus, videoJobId, pollVideoStatus]);

  const handleGenerateVideo = async () => {
    if (!user) { toast.error('Please login to generate videos'); navigate('/login'); return; }
    setGeneratingVideo(true);
    try {
      const res = await videoAPI.generate({ product_id: id, duration: 12 });
      setVideoJobId(res.data.job_id);
      setVideoStatus(res.data.status === 'completed' ? 'completed' : 'processing');
      if (res.data.status !== 'completed') {
        toast.info('AI video generation started! This takes 2-5 minutes.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start video generation');
    } finally {
      setGeneratingVideo(false);
    }
  };

  // Check for story video
  useEffect(() => {
    if (!id) return;
    const checkStory = async () => {
      try {
        const res = await storyAPI.productStory(id);
        if (res.data.status === 'completed') {
          setStoryStatus('completed');
          setStoryJobId(res.data.job_id);
        } else if (res.data.status === 'processing') {
          setStoryStatus('processing');
          setStoryJobId(res.data.job_id);
          setStoryProgress(res.data.progress || 0);
        } else {
          setStoryStatus('none');
        }
      } catch { setStoryStatus('none'); }
    };
    checkStory();
  }, [id]);

  // Poll story status
  useEffect(() => {
    if (storyStatus !== 'processing' || !storyJobId) return;
    storyPollRef.current = setInterval(async () => {
      try {
        const res = await storyAPI.status(storyJobId);
        setStoryProgress(res.data.progress || 0);
        setStoryStep(res.data.current_step || '');
        if (res.data.status === 'completed') {
          setStoryStatus('completed');
          clearInterval(storyPollRef.current);
          toast.success('Story video ready!');
        } else if (res.data.status === 'failed') {
          setStoryStatus('failed');
          clearInterval(storyPollRef.current);
          toast.error('Story generation failed');
        }
      } catch { /* ignore */ }
    }, 10000);
    return () => { if (storyPollRef.current) clearInterval(storyPollRef.current); };
  }, [storyStatus, storyJobId]);

  const handleGenerateStory = async () => {
    if (!user) { toast.error('Please login'); navigate('/login'); return; }
    setGeneratingStory(true);
    try {
      const res = await storyAPI.generate({ product_id: id, scene_count: 5 });
      setStoryJobId(res.data.job_id);
      setStoryStatus(res.data.status === 'completed' ? 'completed' : 'processing');
      if (res.data.status !== 'completed') {
        toast.info('Story video production started! 5 scenes + narration. ~12 min.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start story generation');
    } finally {
      setGeneratingStory(false);
    }
  };

  const handleGetEstimate = async () => {
    if (!deliveryAddress.trim()) {
      toast.error('Please enter a delivery address');
      return;
    }
    setEstimating(true);
    try {
      const res = await deliveryAPI.estimate(deliveryAddress);
      setDeliveryEstimate(res.data);
    } catch {
      toast.error('Could not get delivery estimate');
    } finally {
      setEstimating(false);
    }
  };

  const subtotal = product ? product.price * quantity : 0;
  const deliveryFee = includeDelivery && deliveryEstimate ? deliveryEstimate.delivery_fee : 0;
  const total = subtotal + deliveryFee;

  const handleBuyNow = async () => {
    if (!user) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    if (includeDelivery && !deliveryAddress.trim()) {
      toast.error('Please enter a delivery address');
      return;
    }

    setBuying(true);
    try {
      const orderRes = await ordersAPI.create({
        product_id: id,
        quantity,
        include_delivery: includeDelivery,
        delivery_address: includeDelivery ? deliveryAddress : null,
      });
      toast.success('Order created! Redirecting to payment...');

      const paymentRes = await paymentsAPI.createCheckout(orderRes.data.order_id);
      window.location.href = paymentRes.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-lime border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-30 glass border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        <ShareButton contentType="product" contentId={id} title={product?.name} />
        <ReportButton contentType="product" contentId={id} />
      </div>

      {/* Product Image */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative aspect-square bg-zinc-900"
      >
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag size={80} className="text-zinc-700" />
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4 bg-zinc-900/90 px-3 py-1">
          <span className="text-sm text-zinc-300">{product.category}</span>
        </div>
      </motion.div>

      {/* AI Video Section */}
      <div className="px-4 pt-4">
        {videoStatus === 'completed' && videoJobId && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => setShowVideoPlayer(true)}
              className="w-full relative group overflow-hidden rounded-xl border-2 border-violet-400/50 bg-gradient-to-r from-violet-950 to-zinc-900 p-4 flex items-center gap-4 hover:border-violet-400 transition-all"
              data-testid="watch-ai-video-btn"
            >
              <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center group-hover:bg-violet-500/40 transition-colors">
                <Play size={22} className="text-violet-300 ml-0.5" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-white text-sm">Watch AI Video</p>
                <p className="text-xs text-violet-300">Sora 2 generated product showcase</p>
              </div>
              <Film size={20} className="ml-auto text-violet-400" />
            </button>
          </motion.div>
        )}

        {videoStatus === 'processing' && (
          <div className="w-full rounded-xl border border-amber-400/30 bg-amber-950/30 p-4 flex items-center gap-4" data-testid="video-processing">
            <Loader2 size={22} className="text-amber-400 animate-spin" />
            <div>
              <p className="font-semibold text-amber-200 text-sm">Generating AI Video...</p>
              <p className="text-xs text-amber-400/80">Sora 2 is creating your video. ~2-5 min remaining.</p>
            </div>
          </div>
        )}

        {(videoStatus === 'none' || videoStatus === 'failed') && user && product && user.id === product.vendor_id && (
          <Button
            onClick={handleGenerateVideo}
            disabled={generatingVideo}
            variant="outline"
            className="w-full border-violet-300/50 text-violet-300 hover:bg-violet-500/10 hover:border-violet-400 rounded-xl py-5 bg-zinc-900/50"
            data-testid="generate-ai-video-btn"
          >
            {generatingVideo ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> Starting Generation...</>
            ) : (
              <><Film size={16} className="mr-2" /> Generate AI Product Video</>
            )}
          </Button>
        )}
      </div>

      {/* Video Player Modal */}
      <AnimatePresence>
        {showVideoPlayer && videoJobId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setShowVideoPlayer(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideoPlayer(false)}
                className="absolute -top-10 right-0 text-white/70 hover:text-white p-1"
                data-testid="close-video-btn"
              >
                <X size={24} />
              </button>
              <div className="rounded-2xl overflow-hidden bg-black shadow-2xl shadow-violet-500/20 border border-violet-500/30">
                <video
                  src={videoAPI.fileUrl(videoJobId)}
                  controls
                  autoPlay
                  className="w-full"
                  data-testid="ai-video-player"
                />
                <div className="px-4 py-3 bg-zinc-900 flex items-center gap-2">
                  <Film size={14} className="text-violet-400" />
                  <span className="text-xs text-zinc-400">Powered by Sora 2 AI</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Story Video Section */}
      <div className="px-4 pt-2">
        {storyStatus === 'completed' && storyJobId && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <button
              onClick={() => setShowStoryPlayer(true)}
              className="w-full relative group overflow-hidden rounded-xl border-2 border-pink-400/50 bg-gradient-to-r from-pink-950 to-zinc-900 p-4 flex items-center gap-4 hover:border-pink-400 transition-all"
              data-testid="watch-story-video-btn"
            >
              <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center group-hover:bg-pink-500/40 transition-colors">
                <Play size={22} className="text-pink-300 ml-0.5" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-white text-sm">Watch Story Video</p>
                <p className="text-xs text-pink-300">5-scene cinematic with AI narration</p>
              </div>
              <Clapperboard size={20} className="ml-auto text-pink-400" />
            </button>
          </motion.div>
        )}

        {storyStatus === 'processing' && (
          <div className="w-full rounded-xl border border-pink-400/30 bg-pink-950/20 p-4" data-testid="story-processing">
            <div className="flex items-center gap-3 mb-2">
              <Loader2 size={18} className="text-pink-400 animate-spin" />
              <p className="font-semibold text-pink-200 text-sm">Producing Story Video...</p>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-2 mb-1.5">
              <div className="bg-gradient-to-r from-pink-500 to-violet-500 h-2 rounded-full transition-all duration-500" style={{ width: `${storyProgress}%` }} />
            </div>
            <p className="text-xs text-pink-400/70">{storyStep || `${storyProgress}% — Generating scenes with Sora 2 + AI narration`}</p>
          </div>
        )}

        {(storyStatus === 'none' || storyStatus === 'failed') && user && product && user.id === product.vendor_id && (
          <Button
            onClick={handleGenerateStory}
            disabled={generatingStory}
            variant="outline"
            className="w-full border-pink-300/50 text-pink-300 hover:bg-pink-500/10 hover:border-pink-400 rounded-xl py-5 bg-zinc-900/50"
            data-testid="generate-story-video-btn"
          >
            {generatingStory ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> Starting Production...</>
            ) : (
              <><Clapperboard size={16} className="mr-2" /> Generate Story Video (5 scenes + narration)</>
            )}
          </Button>
        )}
      </div>

      {/* Story Video Player Modal */}
      <AnimatePresence>
        {showStoryPlayer && storyJobId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setShowStoryPlayer(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowStoryPlayer(false)}
                className="absolute -top-10 right-0 text-white/70 hover:text-white p-1"
                data-testid="close-story-btn"
              >
                <X size={24} />
              </button>
              <div className="rounded-2xl overflow-hidden bg-black shadow-2xl shadow-pink-500/20 border border-pink-500/30">
                <video
                  src={storyAPI.fileUrl(storyJobId)}
                  controls
                  autoPlay
                  className="w-full"
                  data-testid="story-video-player"
                />
                <div className="px-4 py-3 bg-zinc-900 flex items-center gap-2">
                  <Clapperboard size={14} className="text-pink-400" />
                  <span className="text-xs text-zinc-400">Story by Sora 2 AI + GPT-5.2 narration</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product Info */}
      <div className="px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Vendor */}
          <p className="text-sm text-zinc-500 mb-2">by {product.vendor_name}</p>
          
          {/* Title */}
          <h1 className="font-display font-bold text-2xl mb-3">{product.name}</h1>
          
          {/* Rating */}
          {product.reviews_count > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i < Math.round(product.rating) ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'}
                  />
                ))}
              </div>
              <span className="text-sm text-zinc-400">
                {product.rating.toFixed(1)} ({product.reviews_count} reviews)
              </span>
            </div>
          )}
          
          {/* Price */}
          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-display font-bold text-3xl text-lime" data-testid="product-price">
              {formatCurrency(product.price)}
            </span>
            <span className="text-sm text-zinc-500">{product.inventory} in stock</span>
          </div>
          
          {/* Description */}
          <p className="text-zinc-400 mb-8">{product.description}</p>
          
          {/* Quantity Selector */}
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm text-zinc-500">Quantity</span>
            <div className="flex items-center border border-zinc-200 rounded-lg">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-2 hover:bg-zinc-100 transition-colors"
                data-testid="qty-minus"
              >
                <Minus size={16} />
              </button>
              <span className="w-12 text-center font-mono" data-testid="qty-value">{quantity}</span>
              <button
                onClick={() => setQuantity(Math.min(product.inventory, quantity + 1))}
                className="p-2 hover:bg-zinc-100 transition-colors"
                data-testid="qty-plus"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* Delivery Option */}
          {user && (
            <div className="mb-6 p-4 bg-zinc-50 rounded-xl border border-zinc-200" data-testid="delivery-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Truck size={18} className="text-blue-500" />
                  <span className="font-semibold text-zinc-900 text-sm">Add Delivery</span>
                </div>
                <button
                  onClick={() => { setIncludeDelivery(!includeDelivery); setDeliveryEstimate(null); }}
                  className={`relative w-11 h-6 rounded-full transition-colors ${includeDelivery ? 'bg-[#b8ff00]' : 'bg-zinc-300'}`}
                  data-testid="delivery-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${includeDelivery ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              {includeDelivery && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <Input
                        placeholder="Enter delivery address..."
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        className="pl-9 bg-white border-zinc-200 text-sm"
                        data-testid="delivery-address-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGetEstimate}
                      disabled={estimating || !deliveryAddress.trim()}
                      className="text-xs whitespace-nowrap"
                      data-testid="get-estimate-btn"
                    >
                      {estimating ? 'Checking...' : 'Get Quote'}
                    </Button>
                  </div>

                  {deliveryEstimate && (
                    <div className="flex items-center justify-between text-sm bg-white p-3 rounded-lg border border-zinc-200" data-testid="delivery-estimate">
                      <div className="flex items-center gap-2">
                        <Package size={14} className="text-green-500" />
                        <span className="text-zinc-700">OVA Rideshare</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-zinc-900">{formatCurrency(deliveryEstimate.delivery_fee)}</span>
                        <span className="text-xs text-zinc-500 ml-2">~{deliveryEstimate.eta_minutes} min</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Price Summary */}
          <div className="mb-4 p-4 bg-zinc-50 rounded-xl border border-zinc-200 space-y-2" data-testid="price-summary">
            <div className="flex justify-between text-sm text-zinc-600">
              <span>Subtotal ({quantity}x)</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {includeDelivery && deliveryEstimate && (
              <div className="flex justify-between text-sm text-zinc-600">
                <span>Delivery</span>
                <span>{formatCurrency(deliveryFee)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-zinc-900 pt-2 border-t border-zinc-200">
              <span>Total</span>
              <span data-testid="total-price">{formatCurrency(total)}</span>
            </div>
          </div>

          {/* Buy Button */}
          <Button
            onClick={handleBuyNow}
            className="w-full bg-[#b8ff00] text-black hover:bg-[#a8ef00] font-bold uppercase tracking-wider py-6 text-base rounded-xl"
            disabled={product.inventory === 0 || buying || (includeDelivery && !deliveryEstimate)}
            data-testid="buy-now-btn"
          >
            {buying ? 'Processing...' : product.inventory > 0 ? `Buy Now - ${formatCurrency(total)}` : 'Out of Stock'}
          </Button>

          {/* Negotiate Button */}
          {user && product.inventory > 0 && product.vendor_id && product.vendor_id !== user.id && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await roomsAPI.create({ product_id: id, seller_id: product.vendor_id, initial_message: `I'd like to negotiate on ${product.name}` });
                  navigate(`/room/${res.data.id}`);
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create room'); }
              }}
              className="w-full mt-2 border-violet-300 text-violet-700 hover:bg-violet-50 font-semibold py-5 rounded-xl"
              data-testid="negotiate-btn"
            >
              <Handshake size={18} className="mr-2" /> Negotiate Price
            </Button>
          )}

          {/* AI Auto-Negotiator */}
          {user && product.inventory > 0 && product.vendor_id && product.vendor_id !== user.id && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowAINeg(!showAINeg)}
                className="w-full mt-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold py-5 rounded-xl"
                data-testid="ai-negotiate-toggle-btn"
              >
                <Sparkles size={18} className="mr-2" /> AI Auto-Negotiator
              </Button>
              {showAINeg && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-2 p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-3">
                  <p className="text-xs text-emerald-700">Set your price range and AI will negotiate the best deal for you. The seller gets real-time notifications.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-emerald-600 font-semibold">Desired Price</label>
                      <Input type="number" value={aiDesiredPrice} onChange={e => setAiDesiredPrice(e.target.value)} placeholder={`$${Math.round(product.price * 0.7)}`} className="text-sm border-emerald-200" data-testid="ai-desired-price" />
                    </div>
                    <div>
                      <label className="text-[10px] text-emerald-600 font-semibold">Max You'll Pay</label>
                      <Input type="number" value={aiMinPrice} onChange={e => setAiMinPrice(e.target.value)} placeholder={`$${Math.round(product.price * 0.85)}`} className="text-sm border-emerald-200" data-testid="ai-max-price" />
                    </div>
                  </div>
                  <Input value={aiNegMsg} onChange={e => setAiNegMsg(e.target.value)} placeholder="Any notes for the AI? (optional)" className="text-sm border-emerald-200" />
                  <Button
                    onClick={async () => {
                      const desired = parseFloat(aiDesiredPrice);
                      const minP = parseFloat(aiMinPrice);
                      if (!desired || !minP) { toast.error('Set both prices'); return; }
                      setStartingNeg(true);
                      try {
                        await negotiateAPI.start({ product_id: id, desired_price: desired, min_price: minP, message: aiNegMsg });
                        toast.success('AI Negotiator started! The seller will be notified.');
                        setShowAINeg(false);
                        navigate('/negotiations');
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start negotiation'); }
                      finally { setStartingNeg(false); }
                    }}
                    disabled={startingNeg}
                    className="w-full bg-emerald-600 text-white font-bold hover:bg-emerald-700"
                    data-testid="start-ai-negotiation-btn"
                  >
                    {startingNeg ? 'Starting...' : 'Let AI Negotiate for Me'}
                  </Button>
                </motion.div>
              )}
            </>
          )}

          {!user && product.inventory > 0 && (
            <p className="text-center text-sm text-zinc-500 mt-3">
              Please <button onClick={() => navigate('/login')} className="font-semibold text-zinc-900 hover:text-[#00aa55]">login</button> to purchase
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
